import { Set } from 'immutable';
import { call } from '../utils/functions';

type EventCallback = (...args: any) => any;

export class EventEmitter<K extends keyof NonNullable<any> = any> {
    private listeners: { [event in K]: Set<EventCallback> } | any = {};

    /**
     * Grab the event listeners, creating empty Set if doesn't exist
     * @param event
     */
    public listener(event: K | string): Set<EventCallback> {
        if (!this.listeners[event]) {
            this.listeners[event] = Set([]);
        }
        return this.listeners[event];
    }

    /**
     * Add listener to the event
     * @param event
     * @param callback
     */
    public subscribe(event: K | string, callback: EventCallback) {
        this.listeners[event] = this.listener(event).add(callback);
        return () => this.unsubscribe(event, callback);
    }

    /**
     * Remove listener to the event
     * @param event
     * @param callback
     */
    public unsubscribe(event: K | string, callback?: EventCallback) {
        if (callback) {
            this.listeners[event] = this.listener(event).remove(callback);
        } else {
            this.listeners[event] = this.listener(event).clear();
        }
    }

    /**
     * Alias to subscribe
     * @param event
     * @param callback
     */
    public addEventListener(event: K | string, callback: EventCallback) {
        return this.subscribe(event, callback);
    }

    /**
     * Alias to unsubscribe
     * @param event
     * @param callback
     */
    public removeEventListener(event: K | string, callback?: EventCallback) {
        return this.unsubscribe(event, callback);
    }

    /**
     * Alias to subscribe
     * @param event
     * @param callback
     */
    public on(event: K | string, callback: EventCallback) {
        return this.subscribe(event, callback);
    }

    /**
     * Alias to unsubscribe
     * @param event
     * @param callback
     */
    public off(event: K | string, callback?: EventCallback) {
        return this.unsubscribe(event, callback);
    }

    /**
     * Emit data to all available event listeners
     * @param event
     * @param props
     */
    public emit(event: K | string, ...props: any) {
        this.listener(event).forEach((callback) => {
            call(callback, ...props);
        });
    }

    /**
     * Alias for Emit
     * @param event
     * @param props
     */
    public trigger(event: K | string, ...props: any) {
        this.emit(event, ...props);
    }

    /**
     * Count all listeners available for the event
     * @param event
     */
    public countListeners(event: K | string): number {
        return this.listener(event).size;
    }

    /**
     * Res emitter
     */
    public reset() {
        Object.keys(this.listeners).forEach((key) => {
            this.unsubscribe(key);
        });

        this.listeners = {};
    }
}
